import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { KeycloakProvider } from '@/auth/KeycloakProvider';
import { MasterclassApolloProvider } from '@/components/ApolloProvider/ApolloProvider';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ROUTES } from '@/config/routes';
import { MasterclassProvider, useMasterclass } from '@/contexts/MasterclassContext';
import { AppHeader } from '@/modules/app-header';
import { ReadOnlyProvider } from '@/modules/read-only';

import { useAppVersion } from './hooks/useAppVersion';
import { RouteObserver } from './modules/google-analytics/analytic/RouteObserver';
import AccessLink from './pages/AccessLink';
import Auth from './pages/Auth';
import CaseSelection from './pages/CaseSelection';
import Index from './pages/Index';
import MasterclassHome from './pages/MasterclassHome';
import MasterclassStatsOverview from './pages/MasterclassStatsOverview';
import NotFound from './pages/NotFound';
import Stats from './pages/Stats';

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { userProfile } = useMasterclass();
  const showHeader = location.pathname !== ROUTES.AUTH;

  return (
    <div className="flex flex-col md:h-screen">
      {showHeader && <AppHeader userProfile={userProfile} />}
      <main className="flex-1 md:overflow-hidden">{children}</main>
    </div>
  );
};

const App = () => {
  useAppVersion();
  return (
    <KeycloakProvider>
      <MasterclassApolloProvider>
        <MasterclassProvider>
          <ReadOnlyProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <RouteObserver />
                <AppLayout>
                  <Routes>
                    {/* Public routes */}
                    <Route path={ROUTES.AUTH} element={<Auth />} />
                    <Route path={ROUTES.ACCESS} element={<AccessLink />} />

                    {/* Protected routes */}
                    <Route
                      path={ROUTES.HOME}
                      element={
                        <ProtectedRoute>
                          <Navigate to={ROUTES.MASTERCLASS_HOME} replace />
                        </ProtectedRoute>
                      }
                    />

                    {/* Masterclass listing */}
                    <Route
                      path={ROUTES.MASTERCLASS_HOME}
                      element={
                        <ProtectedRoute>
                          <MasterclassHome />
                        </ProtectedRoute>
                      }
                    />

                    {/* Cases for specific masterclass */}
                    <Route
                      path={ROUTES.MASTERCLASS_CASES}
                      element={
                        <ProtectedRoute>
                          <CaseSelection />
                        </ProtectedRoute>
                      }
                    />

                    {/* Stats overview for a masterclass - /masterclass/:masterclassId/stats */}
                    <Route
                      path={ROUTES.MASTERCLASS_STATS_OVERVIEW}
                      element={
                        <ProtectedRoute>
                          <MasterclassStatsOverview />
                        </ProtectedRoute>
                      }
                    />

                    {/* Case detail within a masterclass - /masterclass/:masterclassId/:caseId */}
                    <Route
                      path={ROUTES.MASTERCLASS_CASE_DETAIL}
                      element={
                        <ProtectedRoute>
                          <Index />
                        </ProtectedRoute>
                      }
                    />

                    {/* Stats for a case within a masterclass - /masterclass/:masterclassId/:caseId/stats */}
                    <Route
                      path={ROUTES.MASTERCLASS_STATS}
                      element={
                        <ProtectedRoute>
                          <Stats />
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch-all */}
                    <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
                  </Routes>
                </AppLayout>
              </BrowserRouter>
            </TooltipProvider>
          </ReadOnlyProvider>
        </MasterclassProvider>
      </MasterclassApolloProvider>
    </KeycloakProvider>
  );
};

export default App;
